import React, { useState, useCallback, useEffect } from 'react';
import './IndirectMethod_ImportSurveysModal.css';
import { openAIService } from '../services/openAIService';

const IndirectMethod_ImportSurveysModal = ({ onClose, onUploadSuccess, programName, school }) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1=type, 2=upload, 3=courses, 4=processing
  
  // Analysis configuration
  const [analysisType, setAnalysisType] = useState(null); // 'by_program' or 'by_course'
  
  // File management
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [courseMapping, setCourseMapping] = useState({});
  const [programNameInput, setProgramNameInput] = useState(''); // Program name for by_program analysis
  const [fileStatuses, setFileStatuses] = useState({}); // Track individual file progress
  const [batchResults, setBatchResults] = useState(null); // Track final batch results
  
  // UI states
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [processingStatus, setProcessingStatus] = useState('');
  const [showResults, setShowResults] = useState(false); // Show results instead of processing
  const [retryProgress, setRetryProgress] = useState({}); // Track retry progress for individual files
  
  // Timeout handling states
  const [timeoutInfo, setTimeoutInfo] = useState(null); // Current timeout information
  const [showTimeoutModal, setShowTimeoutModal] = useState(false); // Show timeout recovery modal
  const [backgroundProcesses, setBackgroundProcesses] = useState([]); // Track background processes

  // Performance and accessibility states
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [announcements, setAnnouncements] = useState([]); // For screen reader announcements
  const [focusedElement, setFocusedElement] = useState(null); // Track focused element for keyboard nav
  const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false); // Show performance details

  // Cancellation states
  const [isCancelling, setIsCancelling] = useState(false); // Currently cancelling operation
  const [cancellationRequested, setCancellationRequested] = useState(false); // User requested cancellation
  const [showCancellationModal, setShowCancellationModal] = useState(false); // Show cancellation confirmation
  const [cancellableOperations, setCancellableOperations] = useState(new Set()); // Track what can be cancelled

  // Performance monitoring and optimization
  const debouncedProgressUpdate = useCallback(
    openAIService.debounce((progressData) => {
      // Debounced progress updates to prevent excessive re-renders
      setFileStatuses(prevStatuses => {
        const fileStatuses = progressData.files || progressData.file_statuses || [];
        if (fileStatuses.length > 0) {
          const newFileStatuses = { ...prevStatuses };
          fileStatuses.forEach(fileStatus => {
            newFileStatuses[fileStatus.filename] = {
              status: fileStatus.status,
              message: fileStatus.progress || fileStatus.error || getStatusMessage(fileStatus.status),
              analysisId: fileStatus.analysis_id,
              error: fileStatus.error
            };
          });
          return newFileStatuses;
        }
        return prevStatuses;
      });
    }, 500), // Update at most every 500ms
    []
  );

  // Accessibility utilities
  const announceToScreenReader = useCallback((message, priority = 'polite') => {
    const announcement = {
      id: Date.now(),
      message,
      priority,
      timestamp: new Date().toISOString()
    };
    
    setAnnouncements(prev => [...prev, announcement]);
    
    // Remove announcement after it's been read
    setTimeout(() => {
      setAnnouncements(prev => prev.filter(a => a.id !== announcement.id));
    }, 3000);
    
    console.log('📢 Screen reader announcement:', message);
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event) => {
    // Handle modal-level keyboard navigation
    if (event.key === 'Escape' && !isProcessing) {
      onClose();
    } else if (event.key === 'Enter' && event.target.tagName === 'BUTTON') {
      event.target.click();
    }
  }, [isProcessing, onClose]);

  // Performance monitoring
  const updatePerformanceMetrics = useCallback(() => {
    const metrics = openAIService.getPerformanceAnalytics();
    setPerformanceMetrics(metrics);
  }, []);

  // Memory optimization
  const optimizePerformance = useCallback(async () => {
    if (isOptimizing) return;
    
    setIsOptimizing(true);
    announceToScreenReader('Optimizing performance...', 'polite');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for UX
      openAIService.optimizeMemory();
      updatePerformanceMetrics();
      announceToScreenReader('Performance optimization complete', 'polite');
    } catch (error) {
      console.error('Performance optimization failed:', error);
    } finally {
      setIsOptimizing(false);
    }
  }, [isOptimizing, announceToScreenReader, updatePerformanceMetrics]);

  // Auto-optimize memory periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isProcessing && selectedFiles.length === 0) {
        optimizePerformance();
      }
    }, 5 * 60 * 1000); // Every 5 minutes when idle

    return () => clearInterval(interval);
  }, [isProcessing, selectedFiles.length, optimizePerformance]);

  // Track performance metrics
  useEffect(() => {
    updatePerformanceMetrics();
    const interval = setInterval(updatePerformanceMetrics, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [updatePerformanceMetrics]);

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset modal state when it opens
  useEffect(() => {
    console.log('🔄 Modal opened - resetting all state');
    setCurrentStep(1);
    setAnalysisType(null);
    setSelectedFiles([]);
    setCourseMapping({});
    setProgramNameInput(''); // Reset program name input
    setFileStatuses({});
    setBatchResults(null);
    setIsDragActive(false);
    setIsProcessing(false);
    setError('');
    setProcessingStatus('');
    setShowResults(false);
    setRetryProgress({});
    setTimeoutInfo(null);
    setShowTimeoutModal(false);
    setBackgroundProcesses([]);
  }, []); // Empty dependency array means this runs once when component mounts

  console.log('🔄 ImportSurveysModal: Render state', {
    currentStep,
    analysisType,
    selectedFiles: selectedFiles.map(f => ({ name: f.name, size: f.size })),
    courseMapping,
    fileStatuses,
    batchResults,
    isProcessing,
    programName,
    school
  });

  // Get step configuration based on analysis type
  const getStepConfig = () => {
    const allSteps = [
      { number: 1, label: 'Type', description: 'Analysis Type' },
      { number: 2, label: 'Upload', description: 'Upload Files' },
      { number: 3, label: analysisType === 'by_program' ? 'Program' : 'Courses', description: analysisType === 'by_program' ? 'Program Name' : 'Course Names' },
      { number: 4, label: 'Process', description: 'Processing' }
    ];

    // Show all steps for both analysis types
    return allSteps;
  };

  // Get the actual step number accounting for skipped steps
  const getDisplayStep = (stepNumber) => {
    if (analysisType === 'by_program' && stepNumber > 3) {
      return stepNumber - 1; // Step 4 becomes display step 3
    }
    return stepNumber;
  };

  // File validation
  const validateFile = (file) => {
    console.log('🔍 Validating file:', { name: file.name, size: file.size, type: file.type });
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      const error = 'Please select an Excel file (.xlsx or .xls)';
      console.warn('❌ File validation failed - invalid type:', { file: file.name, type: file.type });
      return error;
    }

    if (file.size > maxSize) {
      const error = 'File size must be less than 10MB';
      console.warn('❌ File validation failed - too large:', { file: file.name, size: file.size, maxSize });
      return error;
    }

    console.log('✅ File validation passed:', file.name);
    return null;
  };

  // Handle file selection (multiple files)
  const handleFileSelect = (files, addToExisting = false) => {
    console.log('📁 Handling file selection:', files.length, 'files', addToExisting ? '(adding to existing)' : '(replacing all)');
    
    const fileArray = Array.from(files);
    const maxFiles = 10;
    
    // Calculate current count considering existing files
    const currentCount = addToExisting ? selectedFiles.length : 0;
    const totalCount = currentCount + fileArray.length;
    
    if (totalCount > maxFiles) {
      const error = `Maximum ${maxFiles} files allowed. You currently have ${currentCount} file(s), trying to add ${fileArray.length} more.`;
      console.warn('❌ Too many files selected:', { current: currentCount, adding: fileArray.length, total: totalCount, maxFiles });
      setError(error);
      return;
    }

    // Check for duplicate files if adding to existing
    const existingFileNames = addToExisting ? selectedFiles.map(f => f.name) : [];
    const duplicates = fileArray.filter(file => existingFileNames.includes(file.name));
    
    if (duplicates.length > 0) {
      const error = `Duplicate files detected: ${duplicates.map(f => f.name).join(', ')}`;
      console.warn('❌ Duplicate files:', duplicates.map(f => f.name));
      setError(error);
      return;
    }

    // Validate each file
    const validFiles = [];
    const errors = [];
    
    fileArray.forEach((file, index) => {
      console.log(`🔍 Validating file ${index + 1}/${fileArray.length}:`, file.name);
      const validationError = validateFile(file);
      
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        console.warn(`❌ File ${index + 1} failed validation:`, { file: file.name, error: validationError });
      } else {
        validFiles.push(file);
        console.log(`✅ File ${index + 1} validated:`, file.name);
      }
    });

    if (errors.length > 0) {
      setError(`File validation errors:\n${errors.join('\n')}`);
      console.error('❌ File validation summary:', { totalFiles: fileArray.length, validFiles: validFiles.length, errors });
      return;
    }

    // Update selected files
    const newSelectedFiles = addToExisting ? [...selectedFiles, ...validFiles] : validFiles;
    setSelectedFiles(newSelectedFiles);
    setError('');
    
    // Initialize course mapping for "by_course" analysis
    if (analysisType === 'by_course') {
      const newCourseMapping = addToExisting ? { ...courseMapping } : {};
      validFiles.forEach(file => {
        newCourseMapping[file.name] = newCourseMapping[file.name] || {};
      });
      setCourseMapping(newCourseMapping);
      console.log('📚 Updated course mapping for by_course analysis:', newCourseMapping);
    }

    console.log('✅ File selection completed:', { 
      validFiles: validFiles.length, 
      totalFiles: newSelectedFiles.length,
      fileNames: newSelectedFiles.map(f => f.name),
      analysisType,
      addMode: addToExisting 
    });
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
    console.log('🎯 Drag enter detected');
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    console.log('🎯 Drag leave detected');
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    console.log('🎯 Drop detected with', e.dataTransfer.files.length, 'files');
    // When dropping files, add to existing if we already have files
    handleFileSelect(e.dataTransfer.files, selectedFiles.length > 0);
  }, [selectedFiles, analysisType, courseMapping]);

  // File input change handler for initial upload
  const handleInitialFileInputChange = (e) => {
    console.log('📁 Initial file input change detected with', e.target.files.length, 'files');
    handleFileSelect(e.target.files, false); // Replace all files
    // Reset the input so the same files can be selected again if needed
    e.target.value = '';
  };

  // File input change handler for adding more files
  const handleAddMoreFileInputChange = (e) => {
    console.log('📁 Add more file input change detected with', e.target.files.length, 'files');
    handleFileSelect(e.target.files, true); // Add to existing files
    // Reset the input so the same files can be selected again if needed
    e.target.value = '';
  };

  // Step navigation
  const goToStep = (step) => {
    console.log('📍 Navigating to step:', { from: currentStep, to: step });
    setCurrentStep(step);
    setError(''); // Clear errors when changing steps
  };

  // Analysis type selection
  const handleAnalysisTypeSelect = (type) => {
    console.log('🎯 Analysis type selected:', type);
    setAnalysisType(type);
    setSelectedFiles([]); // Reset files when changing type
    setCourseMapping({}); // Reset course mapping
    goToStep(2); // Move to upload step
  };

  // Course name input handling
  const handleCourseNameChange = (fileName, courseName) => {
    console.log('📚 Course name changed:', { fileName, courseName });
    setCourseMapping(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        courseName: courseName
      }
    }));
  };

  // Display name input handling
  const handleDisplayNameChange = (fileName, displayName) => {
    console.log('🏷️ Display name changed:', { fileName, displayName });
    setCourseMapping(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        displayName: displayName
      }
    }));
  };

  // Remove file
  const handleRemoveFile = (fileToRemove) => {
    console.log('🗑️ Removing file:', fileToRemove.name);
    setSelectedFiles(prev => prev.filter(file => file !== fileToRemove));
    
    // Remove from course mapping if exists
    setCourseMapping(prev => {
      const newMapping = { ...prev };
      delete newMapping[fileToRemove.name];
      console.log('📚 Updated course mapping after file removal:', newMapping);
      return newMapping;
    });
  };

  // Validate step progression
  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return analysisType !== null;
      case 2:
        return selectedFiles.length > 0;
      case 3:
        if (analysisType === 'by_course') {
          const allCoursesNamed = selectedFiles.every(file => 
            courseMapping[file.name] && courseMapping[file.name].courseName && courseMapping[file.name].courseName.trim() !== ''
          );
          console.log('📚 Course naming validation:', { 
            allCoursesNamed, 
            requiredCourses: selectedFiles.map(f => f.name),
            currentMapping: courseMapping 
          });
          return allCoursesNamed;
        } else if (analysisType === 'by_program') {
          const programNameValid = programNameInput && programNameInput.trim() !== '';
          console.log('🎓 Program naming validation:', { 
            programNameValid, 
            programName: programNameInput 
          });
          return programNameValid;
        }
        return true;
      default:
        return false;
    }
  };

  // Proceed to next step
  const handleNextStep = () => {
    console.log('▶️ Attempting to proceed from step:', currentStep);
    
    if (!canProceedToNextStep()) {
      const missingRequirements = {
        1: 'Please select an analysis type',
        2: 'Please select at least one file',
        3: analysisType === 'by_program' ? 'Please enter a program name' : 'Please enter course names for all files'
      };
      const error = missingRequirements[currentStep];
      console.warn('❌ Cannot proceed to next step:', { currentStep, reason: error });
      setError(error);
      return;
    }

    if (currentStep === 3) {
      // From course/program naming to processing
      console.log('▶️ Moving from configuration to processing');
      goToStep(4);
      handleStartProcessing();
    } else {
      // Normal step progression
      goToStep(currentStep + 1);
    }
  };

  // Start processing
  const handleStartProcessing = async () => {
    console.log('🚀 Starting processing with configuration:', {
      analysisType,
      fileCount: selectedFiles.length,
      courseMapping: analysisType === 'by_course' ? courseMapping : 'N/A (by_program)',
      programName: analysisType === 'by_program' ? programNameInput : programName,
      school
    });

    setIsProcessing(true);
    setProcessingStatus('Preparing upload...');
    announceToScreenReader(`Starting analysis of ${selectedFiles.length} files`, 'assertive');

    // Add cancellable operation
    const operationId = `batch-upload-${Date.now()}`;
    addCancellableOperation(operationId);

    // Initialize file statuses
    const initialStatuses = {};
    selectedFiles.forEach(file => {
      initialStatuses[file.name] = {
        status: 'queued',
        message: 'Queued for processing'
      };
    });
    setFileStatuses(initialStatuses);

    try {
      // Check for cancellation before starting
      if (cancellationRequested) {
        console.log('🚫 Processing cancelled before start');
        return;
      }

      // Start batch upload with performance tracking
      console.log('📤 Starting batch upload...');
      setProcessingStatus('Uploading files...');
      announceToScreenReader('Uploading files to server', 'polite');
      
      // Format course mapping for backend
      const formattedCourseMapping = {};
      if (analysisType === 'by_course' && courseMapping) {
        Object.entries(courseMapping).forEach(([filename, mapping]) => {
          if (mapping.courseName) {
            formattedCourseMapping[filename] = {
              course_name: mapping.courseName,
              display_name: mapping.displayName || mapping.courseName
            };
          }
        });
      }
      
      // Use performance-tracked batch import with cancellation support
      const pollResult = await openAIService.batchImportWithPerformanceTracking(
        selectedFiles,
        analysisType,
        formattedCourseMapping,
        analysisType === 'by_program' ? programNameInput : programName, // Use programNameInput for by_program analysis
        school,
        (progressData) => {
          // Check for cancellation during progress updates
          if (cancellationRequested) {
            console.log('🚫 Processing cancelled during progress update');
            return;
          }

          // Use debounced progress updates for better performance
          debouncedProgressUpdate(progressData);
          
          // Handle timeout information
          if (progressData.timeoutInfo) {
            setTimeoutInfo(progressData.timeoutInfo);
          }
          
          // Update overall progress with accessibility announcements
          const fileStatuses = progressData.files || progressData.file_statuses || [];
          if (fileStatuses.length > 0) {
            const completedFiles = fileStatuses.filter(f => f.status === 'completed').length;
            const totalFiles = fileStatuses.length;
            const elapsed = progressData.timeoutInfo ? Math.round(progressData.timeoutInfo.elapsed / 1000) : 0;
            
            const statusMessage = `Processing files... (${completedFiles}/${totalFiles} completed)${elapsed > 0 ? ` - ${elapsed}s elapsed` : ''}`;
            setProcessingStatus(statusMessage);
            
            // Announce progress milestones
            const progressPercent = Math.round((completedFiles / totalFiles) * 100);
            if (progressPercent > 0 && progressPercent % 25 === 0) {
              announceToScreenReader(`${progressPercent}% of files processed`, 'polite');
            }
          } else if (progressData.progress_summary) {
            setProcessingStatus(progressData.progress_summary);
          } else if (progressData.status_counts) {
            const completed = progressData.status_counts.completed || 0;
            const failed = progressData.status_counts.failed || 0;
            const total = progressData.total_files || selectedFiles.length;
            const elapsed = progressData.timeoutInfo ? Math.round(progressData.timeoutInfo.elapsed / 1000) : 0;
            setProcessingStatus(`Processing files... (${completed + failed}/${total} completed)${elapsed > 0 ? ` - ${elapsed}s elapsed` : ''}`);
          }
        },
        async (timeoutWarning) => {
          // Check for cancellation during timeout handling
          if (cancellationRequested) {
            console.log('🚫 Processing cancelled during timeout warning');
            return 'cancel';
          }

          // Handle timeout warnings and recovery options
          console.log('⏰ Timeout warning:', timeoutWarning);
          
          if (timeoutWarning.type === 'warning') {
            // Show warning notification
            setTimeoutInfo({
              type: 'warning',
              message: timeoutWarning.message,
              elapsed: timeoutWarning.elapsed,
              remaining: timeoutWarning.remaining
            });
            announceToScreenReader('Processing is taking longer than expected', 'assertive');
            return null; // Continue processing
          } else if (timeoutWarning.type === 'soft') {
            // Show recovery options modal
            announceToScreenReader('Processing timeout - please choose how to proceed', 'assertive');
            return await handleTimeoutRecovery(timeoutWarning);
          }
        }
      );

// <<<<<<< HEAD

      // //console.log('✅ Upload successful:', uploadResult);
      // setCurrentAnalysisId(uploadResult.analysisId);
      // setProcessingStatus('Analyzing survey data...');

      // // Step 2: Poll for completion with real-time status updates
      // const pollResult = await openAIService.pollForCompletion(
      //   uploadResult.analysisId,
      //   school,
      //   (statusData) => {
      //     // Real-time status updates
      //     //console.log('📊 Status update:', statusData);

      // Check for cancellation after processing
      if (cancellationRequested) {
        console.log('🚫 Processing cancelled after completion');
        return;
      }

      if (pollResult.success) {
        console.log('✅ Batch processing completed successfully:', pollResult.data);
        announceToScreenReader('Processing completed successfully', 'assertive');
        
        // Handle successful completion
        const batchData = pollResult.data;
        const allFiles = batchData.files || [];
        const successfulFiles = allFiles.filter(f => f.status === 'completed');
        const failedFiles = allFiles.filter(f => f.status === 'failed');

        // Store batch results for display
        setBatchResults({
          batchId: batchData.batch_id,
          totalFiles: allFiles.length,
          successfulFiles: successfulFiles,
          failedFiles: failedFiles,
          analysisType: analysisType,
          courseMapping: formattedCourseMapping,
          batchData: batchData
        });

        // Show results display instead of closing modal
        setShowResults(true);
        setIsProcessing(false);
        setProcessingStatus('');
        
        // Announce final results
        if (failedFiles.length === 0) {
          announceToScreenReader(`All ${successfulFiles.length} files processed successfully`, 'assertive');
        } else {
          announceToScreenReader(`${successfulFiles.length} files succeeded, ${failedFiles.length} files failed`, 'assertive');
        }
        
        console.log('📊 Batch results prepared:', {
          successful: successfulFiles.length,
          failed: failedFiles.length,
          total: allFiles.length
        });
      } else {
        // Handle failure or timeout
        console.error('❌ Batch processing failed:', pollResult.error);
        
        if (pollResult.timeout) {
          // Hard timeout reached
          setError(`Processing timeout: ${pollResult.error}`);
          setTimeoutInfo({
            type: 'hard',
            message: 'Processing has exceeded the maximum allowed time',
            batchId: pollResult.batchId
          });
          announceToScreenReader('Processing timed out', 'assertive');
        } else if (pollResult.background) {
          // Processing moved to background
          console.log('🔄 Processing moved to background');

// =======
      // //console.log('✅ Upload successful:', uploadResult);
      // setCurrentAnalysisId(uploadResult.analysisId);
      // setProcessingStatus('Analyzing survey data...');

      // // Step 2: Poll for completion with real-time status updates
      // const pollResult = await openAIService.pollForCompletion(
      //   uploadResult.analysisId,
      //   school,
      //   (statusData) => {
      //     // Real-time status updates
      //     //console.log('📊 Status update:', statusData);
// >>>>>>> origin/main
          
          // Start background monitoring
          const backgroundProcess = openAIService.startBackgroundProcessing(
            pollResult.batchId,
            school,
            selectedFiles.length,
            {
              analysisType,
              courseMapping: formattedCourseMapping,
              programName: analysisType === 'by_program' ? programNameInput : programName, // Use correct program name
              selectedFiles: selectedFiles.map(f => ({ name: f.name, size: f.size }))
            }
          );
          
          setBackgroundProcesses(prev => [...prev, backgroundProcess]);
          setIsProcessing(false);
          setProcessingStatus('');
          setError('Processing moved to background. You will be notified when complete.');
          announceToScreenReader('Processing moved to background - you will be notified when complete', 'assertive');
          
          // Request notification permission
          openAIService.requestNotificationPermission();
          
        } else if (pollResult.cancelled) {
          // User cancelled processing
          setError('Processing cancelled by user');
          announceToScreenReader('Processing cancelled', 'assertive');
        } else if (pollResult.networkError) {
          // Network error during processing
          setError(`Network error: ${pollResult.error}`);
          announceToScreenReader('Network error occurred during processing', 'assertive');
        } else {
          // Other processing failure
          setError(`Processing failed: ${pollResult.error}`);
          announceToScreenReader('Processing failed', 'assertive');
        }
      }
      
    } catch (error) {
      console.error('❌ Processing failed:', error);
      setError(`Processing failed: ${error.message}`);
      announceToScreenReader('Processing failed due to an error', 'assertive');
    } finally {
      // Remove cancellable operation
      removeCancellableOperation(operationId);
      setIsProcessing(false);
      setProcessingStatus('');
      // Update performance metrics after processing
      updatePerformanceMetrics();
    }
  };

  // Helper function to get status message
  const getStatusMessage = (status) => {
    switch (status) {
      case 'queued': return 'Queued for processing';
      case 'processing': return 'Analyzing with AI...';
      case 'completed': return 'Analysis complete';
      case 'failed': return 'Processing failed';
      default: return 'Unknown status';
    }
  };

  // Handle timeout recovery options
  const handleTimeoutRecovery = async (timeoutWarning) => {
    return new Promise((resolve) => {
      setTimeoutInfo({
        type: 'soft',
        message: timeoutWarning.message,
        elapsed: timeoutWarning.elapsed,
        remaining: timeoutWarning.remaining,
        batchId: timeoutWarning.batchId,
        batchStatus: timeoutWarning.batchStatus,
        options: timeoutWarning.options,
        onResolve: resolve
      });
      setShowTimeoutModal(true);
    });
  };

  // Handle timeout modal actions
  const handleTimeoutAction = (action) => {
    console.log('⏰ Timeout action selected:', action);
    
    if (timeoutInfo && timeoutInfo.onResolve) {
      timeoutInfo.onResolve(action);
    }
    
    setShowTimeoutModal(false);
    setTimeoutInfo(null);
  };

  // Results action handlers
  const handleViewResults = (analysisId, filename) => {
    console.log('👁️ Viewing results for:', { analysisId, filename });
    
    // Close modal and trigger success callback to navigate to results
    onUploadSuccess({
      fileName: filename,
      message: `Analysis complete for ${filename}`,
      analysisType: batchResults.analysisType,
      courseMapping: batchResults.courseMapping,
      analysisId: analysisId,
      isBatch: false // Individual file view
    });
  };

  const handleViewAllResults = () => {
    console.log('👁️ Viewing all successful results');
    
    // Close modal and trigger success callback with batch data
    onUploadSuccess({
      fileName: batchResults.successfulFiles.map(f => f.filename).join(', '),
      message: `Successfully processed ${batchResults.successfulFiles.length} file(s)`,
      analysisType: batchResults.analysisType,
      courseMapping: batchResults.courseMapping,
      batchData: batchResults.batchData,
      isBatch: true
    });
  };

  const handleRetryFile = async (filename) => {
    console.log('🔄 Retrying file with advanced logic:', filename);
    
    // Add cancellable operation
    const operationId = `retry-file-${filename}-${Date.now()}`;
    addCancellableOperation(operationId);
    
    // Find the original file
    const originalFile = selectedFiles.find(f => f.name === filename);
    if (!originalFile) {
      setError(`Could not find original file: ${filename}`);
      removeCancellableOperation(operationId);
      return;
    }

    // Initialize retry progress tracking
    setRetryProgress(prev => ({
      ...prev,
      [filename]: {
        status: 'starting',
        attempt: 0,
        maxAttempts: 3,
        message: 'Initializing retry...',
        retryHistory: [],
        errorCategory: null,
        delay: 0
      }
    }));

    // Reset file status to show retry in progress
    setFileStatuses(prev => ({
      ...prev,
      [filename]: {
        status: 'processing',
        message: 'Starting retry with advanced logic...'
      }
    }));

    try {
      // Check for cancellation before starting retry
      if (cancellationRequested) {
        console.log('🚫 Retry cancelled before start');
        return;
      }

      // Prepare course mapping for this file
      const formattedCourseMapping = {};
      if (batchResults.analysisType === 'by_course' && batchResults.courseMapping[filename]) {
        formattedCourseMapping[filename] = batchResults.courseMapping[filename];
      }

      // Use advanced retry logic
      const retryResult = await openAIService.retryFileWithAdvancedLogic(
        originalFile,
        batchResults.analysisType,
        formattedCourseMapping,
        programName,
        school,
        (retryUpdate) => {
          // Check for cancellation during retry updates
          if (cancellationRequested) {
            console.log('🚫 Retry cancelled during update');
            return;
          }

          console.log('🔄 Retry update for', filename, ':', retryUpdate);
          
          // Update retry progress based on update type
          switch (retryUpdate.type) {
            case 'error':
              setRetryProgress(prev => ({
                ...prev,
                [filename]: {
                  ...prev[filename],
                  status: 'error',
                  attempt: retryUpdate.attempt,
                  message: `Attempt ${retryUpdate.attempt} failed: ${retryUpdate.error}`,
                  errorCategory: retryUpdate.errorCategory,
                  retryHistory: retryUpdate.retryHistory,
                  willRetry: retryUpdate.willRetry
                }
              }));
              
              setFileStatuses(prev => ({
                ...prev,
                [filename]: {
                  status: retryUpdate.willRetry ? 'processing' : 'failed',
                  message: retryUpdate.willRetry ? 
                    `Attempt ${retryUpdate.attempt} failed, will retry...` :
                    `Failed after ${retryUpdate.attempt} attempts: ${retryUpdate.error}`
                }
              }));
              break;

            case 'delay':
              // Check for cancellation during delay
              if (cancellationRequested) {
                console.log('🚫 Retry cancelled during delay');
                return;
              }

              setRetryProgress(prev => ({
                ...prev,
                [filename]: {
                  ...prev[filename],
                  status: 'waiting',
                  delay: retryUpdate.delay,
                  nextAttempt: retryUpdate.nextAttempt,
                  message: `Waiting ${retryUpdate.delay / 1000}s before attempt ${retryUpdate.nextAttempt}...`
                }
              }));
              
              setFileStatuses(prev => ({
                ...prev,
                [filename]: {
                  status: 'processing',
                  message: `Waiting ${retryUpdate.delay / 1000}s before retry ${retryUpdate.nextAttempt}...`
                }
              }));
              break;

            case 'success':
              setRetryProgress(prev => ({
                ...prev,
                [filename]: {
                  ...prev[filename],
                  status: 'upload_success',
                  attempt: retryUpdate.attempt,
                  message: `Upload succeeded on attempt ${retryUpdate.attempt}`,
                  retryHistory: retryUpdate.retryHistory
                }
              }));
              
              setFileStatuses(prev => ({
                ...prev,
                [filename]: {
                  status: 'processing',
                  message: 'Upload successful, processing analysis...'
                }
              }));
              break;

            case 'progress':
              // Handle batch processing progress
              const progressData = retryUpdate.progressData;
              const fileStatuses = progressData.files || [];
              const fileStatus = fileStatuses.find(f => f.filename === filename);
              
              if (fileStatus) {
                setFileStatuses(prev => ({
                  ...prev,
                  [filename]: {
                    status: fileStatus.status,
                    message: fileStatus.progress || fileStatus.error || getStatusMessage(fileStatus.status),
                    analysisId: fileStatus.analysis_id,
                    error: fileStatus.error
                  }
                }));

                if (fileStatus.status === 'completed' || fileStatus.status === 'failed') {
                  setRetryProgress(prev => ({
                    ...prev,
                    [filename]: {
                      ...prev[filename],
                      status: fileStatus.status,
                      message: fileStatus.status === 'completed' ? 
                        'Retry successful - analysis complete!' :
                        `Retry failed: ${fileStatus.error || 'Unknown error'}`
                    }
                  }));
                }
              }
              break;
          }
        }
      );

// <<<<<<< HEAD
// // <<<<<<< Updated upstream
//       // if (pollResult.success) {
//       //   // Analysis completed successfully
//       //   //console.log('✅ Analysis completed:', pollResult.data);
// =======
      // if (pollResult.success) {
        // Analysis completed successfully
        //console.log('✅ Analysis completed:', pollResult.data);
// >>>>>>> origin/main
        
      //   onUploadSuccess({
      //     fileName: selectedFile.name,
      //     message: 'Survey analysis completed successfully!',
      //     analysisData: pollResult.data,
      //     analysisId: uploadResult.analysisId
      //   });
      // } else {
      //   // Analysis failed
      //   const formattedError = openAIService.getFormattedErrorMessage(pollResult.error);
      //   setError(formattedError);
// =======
      // Check for cancellation after retry
      if (cancellationRequested) {
        console.log('🚫 Retry cancelled after completion');
        return;
// >>>>>>> Stashed changes
      }

      if (retryResult.success) {
        // Update batch results with retry success
        const retryData = retryResult.data;
        const retryFiles = retryData.files || [];
        const retryFile = retryFiles.find(f => f.filename === filename);
        
        if (retryFile && retryFile.status === 'completed') {
          // Move from failed to successful
          setBatchResults(prev => ({
            ...prev,
            successfulFiles: [...prev.successfulFiles, retryFile],
            failedFiles: prev.failedFiles.filter(f => f.filename !== filename)
          }));
          
          // Update retry progress to show final success
          setRetryProgress(prev => ({
            ...prev,
            [filename]: {
              ...prev[filename],
              status: 'completed',
              message: `Retry successful after ${retryResult.totalAttempts} attempt(s)!`,
              finalResult: retryFile
            }
          }));
          
          console.log('✅ Advanced retry successful for:', filename, 'after', retryResult.totalAttempts, 'attempts');
        } else {
          // Retry succeeded but file still failed processing
          setRetryProgress(prev => ({
            ...prev,
            [filename]: {
              ...prev[filename],
              status: 'failed',
              message: 'Retry uploaded successfully but processing failed'
            }
          }));
        }
      } else {
        // All retry attempts exhausted
        const errorCategory = retryResult.finalErrorCategory;
        const isRetryable = openAIService.isRetryable ? openAIService.isRetryable(errorCategory) : true;
        
        setRetryProgress(prev => ({
          ...prev,
          [filename]: {
            ...prev[filename],
            status: 'failed',
            message: `All retry attempts exhausted. ${isRetryable ? 'You can try again later.' : 'This error is not retryable.'}`,
            errorCategory,
            isRetryable,
            finalError: retryResult.error
          }
        }));
        
        setFileStatuses(prev => ({
          ...prev,
          [filename]: {
            status: 'failed',
            message: `Retry failed: ${retryResult.error?.message || 'Unknown error'}`,
            error: retryResult.error?.message
          }
        }));
        
        console.error('❌ Advanced retry failed for:', filename, 'after', retryResult.attempts, 'attempts');
      }
      
    } catch (error) {
      console.error('❌ Advanced retry error:', error);
      
      setRetryProgress(prev => ({
        ...prev,
        [filename]: {
          ...prev[filename],
          status: 'failed',
          message: `Retry error: ${error.message}`,
          finalError: error
        }
      }));
      
      setFileStatuses(prev => ({
        ...prev,
        [filename]: {
          status: 'failed',
          message: `Retry error: ${error.message}`,
          error: error.message
        }
      }));
    } finally {
      // Remove cancellable operation
      removeCancellableOperation(operationId);
    }
  };

  const handleRetryAllFailed = async () => {
    console.log('🔄 Retrying all failed files with advanced logic');
    
    const failedFilenames = batchResults.failedFiles.map(f => f.filename);
    const failedOriginalFiles = selectedFiles.filter(f => failedFilenames.includes(f.name));
    
    if (failedOriginalFiles.length === 0) {
      setError('No failed files found to retry');
      return;
    }

    // Reset processing state
    setShowResults(false);
    setIsProcessing(true);
    setProcessingStatus(`Starting advanced retry for ${failedOriginalFiles.length} failed files...`);

    // Initialize retry progress for all failed files
    const initialRetryProgress = {};
    failedOriginalFiles.forEach(file => {
      initialRetryProgress[file.name] = {
        status: 'queued',
        attempt: 0,
        maxAttempts: 3,
        message: 'Queued for advanced retry...',
        retryHistory: [],
        errorCategory: null
      };
    });
    setRetryProgress(initialRetryProgress);

    // Reset file statuses for failed files
    const resetStatuses = {};
    failedOriginalFiles.forEach(file => {
      resetStatuses[file.name] = {
        status: 'queued',
        message: 'Queued for advanced retry'
      };
    });
    setFileStatuses(resetStatuses);

    try {
      // Prepare course mapping for failed files
      const formattedCourseMapping = {};
      if (batchResults.analysisType === 'by_course') {
        failedOriginalFiles.forEach(file => {
          if (batchResults.courseMapping[file.name]) {
            formattedCourseMapping[file.name] = batchResults.courseMapping[file.name];
          }
        });
      }

      // Use advanced retry logic for multiple files
      const retryResults = await openAIService.retryMultipleFilesWithAdvancedLogic(
        failedOriginalFiles,
        batchResults.analysisType,
        formattedCourseMapping,
        programName,
        school,
        (retryUpdate) => {
          console.log('🔄 Multi-file retry update:', retryUpdate);
          
          switch (retryUpdate.type) {
            case 'queue_update':
              // Update overall progress
              setProcessingStatus(`Processing ${retryUpdate.currentFile} (${retryUpdate.currentIndex + 1}/${retryUpdate.totalFiles})`);
              
              // Update retry progress for queue status
              setRetryProgress(prev => {
                const updated = { ...prev };
                retryUpdate.queue.forEach(queueItem => {
                  if (updated[queueItem.filename]) {
                    updated[queueItem.filename] = {
                      ...updated[queueItem.filename],
                      status: queueItem.status,
                      attempt: queueItem.attempts
                    };
                  }
                });
                return updated;
              });
              break;

            case 'error':
              if (retryUpdate.currentFile) {
                setRetryProgress(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    ...prev[retryUpdate.currentFile],
                    status: 'error',
                    attempt: retryUpdate.attempt,
                    message: `Attempt ${retryUpdate.attempt} failed: ${retryUpdate.error}`,
                    errorCategory: retryUpdate.errorCategory,
                    retryHistory: retryUpdate.retryHistory,
                    willRetry: retryUpdate.willRetry
                  }
                }));
                
                setFileStatuses(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    status: retryUpdate.willRetry ? 'processing' : 'failed',
                    message: retryUpdate.willRetry ? 
                      `Attempt ${retryUpdate.attempt} failed, will retry...` :
                      `Failed after ${retryUpdate.attempt} attempts: ${retryUpdate.error}`
                  }
                }));
              }
              break;

            case 'delay':
              if (retryUpdate.currentFile) {
                setRetryProgress(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    ...prev[retryUpdate.currentFile],
                    status: 'waiting',
                    delay: retryUpdate.delay,
                    nextAttempt: retryUpdate.nextAttempt,
                    message: `Waiting ${retryUpdate.delay / 1000}s before attempt ${retryUpdate.nextAttempt}...`
                  }
                }));
                
                setFileStatuses(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    status: 'processing',
                    message: `Waiting ${retryUpdate.delay / 1000}s before retry ${retryUpdate.nextAttempt}...`
                  }
                }));
              }
              break;

            case 'success':
              if (retryUpdate.currentFile) {
                setRetryProgress(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    ...prev[retryUpdate.currentFile],
                    status: 'upload_success',
                    attempt: retryUpdate.attempt,
                    message: `Upload succeeded on attempt ${retryUpdate.attempt}`,
                    retryHistory: retryUpdate.retryHistory
                  }
                }));
                
                setFileStatuses(prev => ({
                  ...prev,
                  [retryUpdate.currentFile]: {
                    status: 'processing',
                    message: 'Upload successful, processing analysis...'
                  }
                }));
              }
              break;

            case 'progress':
              if (retryUpdate.currentFile && retryUpdate.progressData) {
                const progressData = retryUpdate.progressData;
                const fileStatuses = progressData.files || [];
                const fileStatus = fileStatuses.find(f => f.filename === retryUpdate.currentFile);
                
                if (fileStatus) {
                  setFileStatuses(prev => ({
                    ...prev,
                    [retryUpdate.currentFile]: {
                      status: fileStatus.status,
                      message: fileStatus.progress || fileStatus.error || getStatusMessage(fileStatus.status),
                      analysisId: fileStatus.analysis_id,
                      error: fileStatus.error
                    }
                  }));

                  if (fileStatus.status === 'completed' || fileStatus.status === 'failed') {
                    setRetryProgress(prev => ({
                      ...prev,
                      [retryUpdate.currentFile]: {
                        ...prev[retryUpdate.currentFile],
                        status: fileStatus.status,
                        message: fileStatus.status === 'completed' ? 
                          'Retry successful - analysis complete!' :
                          `Retry failed: ${fileStatus.error || 'Unknown error'}`
                      }
                    }));
                  }
                }
              }
              break;
          }
        }
      );

      // Process retry results
      const newSuccessful = [];
      const stillFailed = [];

      retryResults.successful.forEach(successItem => {
        // Find the corresponding file data from the result
        const resultFiles = successItem.result.files || [];
        const resultFile = resultFiles.find(f => f.filename === successItem.file.name);
        if (resultFile) {
          newSuccessful.push(resultFile);
        }
      });

      retryResults.failed.forEach(failedItem => {
        stillFailed.push({
          filename: failedItem.file.name,
          error: failedItem.error?.message || 'Unknown error',
          attempts: failedItem.attempts,
          errorCategory: failedItem.errorCategory
        });
      });

      // Update batch results with retry outcomes
      setBatchResults(prev => ({
        ...prev,
        successfulFiles: [...prev.successfulFiles, ...newSuccessful],
        failedFiles: stillFailed
      }));

      // Update final retry progress
      setRetryProgress(prev => {
        const updated = { ...prev };
        retryResults.successful.forEach(item => {
          updated[item.file.name] = {
            ...updated[item.file.name],
            status: 'completed',
            message: `Retry successful after ${item.attempts} attempt(s)!`,
            finalResult: item.result
          };
        });
        retryResults.failed.forEach(item => {
          const isRetryable = openAIService.isRetryable ? 
            openAIService.isRetryable(item.errorCategory) : true;
          updated[item.file.name] = {
            ...updated[item.file.name],
            status: 'failed',
            message: `All retry attempts exhausted. ${isRetryable ? 'You can try again later.' : 'This error is not retryable.'}`,
            errorCategory: item.errorCategory,
            isRetryable,
            finalError: item.error
          };
        });
        return updated;
      });

      setShowResults(true);
      setIsProcessing(false);
      setProcessingStatus('');

      console.log('🏁 Advanced retry all completed:', {
        successful: newSuccessful.length,
        failed: stillFailed.length,
        total: failedOriginalFiles.length
      });

    } catch (error) {
      console.error('❌ Advanced retry all failed:', error);
      setError(`Advanced retry failed: ${error.message}`);
      setIsProcessing(false);
      setShowResults(true);
    }
  };

  const handleUploadMore = () => {
    console.log('📁 Upload more files requested');
    
    // Reset to file upload step but keep successful results
    setShowResults(false);
    setCurrentStep(2);
    setSelectedFiles([]);
    setCourseMapping({});
    setFileStatuses({});
    setError('');
    setProcessingStatus('');
  };

  // Format file size helper
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render performance dashboard
  const renderPerformanceDashboard = () => {
    if (!performanceMetrics || !showPerformanceDashboard) return null;

    return (
      <div className="indirect_method_ImportSurveysModal_performance_dashboard">
        <div className="indirect_method_ImportSurveysModal_dashboard_header">
          <h4>Performance Metrics</h4>
          <button 
            className="indirect_method_ImportSurveysModal_dashboard_close"
            onClick={() => setShowPerformanceDashboard(false)}
            aria-label="Close performance dashboard"
          >
            ×
          </button>
        </div>
        
        <div className="indirect_method_ImportSurveysModal_dashboard_content">
          {/* Upload Metrics */}
          <div className="indirect_method_ImportSurveysModal_metric_section">
            <h5>📤 Upload Performance</h5>
            <div className="indirect_method_ImportSurveysModal_metric_grid">
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Total Uploads:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {performanceMetrics.uploads.total}
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Success Rate:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {performanceMetrics.uploads.total > 0 ? 
                    Math.round((performanceMetrics.uploads.successful / performanceMetrics.uploads.total) * 100) : 0}%
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Avg Duration:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {(performanceMetrics.uploads.avgDuration / 1000).toFixed(1)}s
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Throughput:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {(performanceMetrics.uploads.avgThroughput / 1024 / 1024).toFixed(2)}MB/s
                </span>
              </div>
            </div>
          </div>

          {/* Memory Metrics */}
          <div className="indirect_method_ImportSurveysModal_metric_section">
            <h5>🧠 Memory Usage</h5>
            <div className="indirect_method_ImportSurveysModal_metric_grid">
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Current:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {performanceMetrics.memory.current ? 
                    Math.round(performanceMetrics.memory.current.used / 1024 / 1024) : 0}MB
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Peak:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {Math.round(performanceMetrics.memory.peak / 1024 / 1024)}MB
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Average:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {Math.round(performanceMetrics.memory.avg / 1024 / 1024)}MB
                </span>
              </div>
              <div className="indirect_method_ImportSurveysModal_metric_item">
                <span className="indirect_method_ImportSurveysModal_metric_label">Usage:</span>
                <span className="indirect_method_ImportSurveysModal_metric_value">
                  {performanceMetrics.memory.current ? 
                    Math.round((performanceMetrics.memory.current.used / performanceMetrics.memory.current.limit) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Background Processes */}
          {backgroundProcesses.length > 0 && (
            <div className="indirect_method_ImportSurveysModal_metric_section">
              <h5>🔄 Background Processes</h5>
              <div className="indirect_method_ImportSurveysModal_background_list">
                {backgroundProcesses.map((process, index) => (
                  <div key={index} className="indirect_method_ImportSurveysModal_background_item">
                    <span className="indirect_method_ImportSurveysModal_background_id">
                      {process.batchId.substring(0, 8)}...
                    </span>
                    <span className="indirect_method_ImportSurveysModal_background_status">
                      {process.status}
                    </span>
                    <span className="indirect_method_ImportSurveysModal_background_time">
                      {Math.round((Date.now() - process.startTime) / 1000)}s
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    console.log('🎨 Rendering step content for step:', currentStep);
    
    // Show results if processing is complete
    if (showResults && batchResults) {
      return renderResults();
    }
    
    switch (currentStep) {
      case 1:
        return renderAnalysisTypeSelection();
      case 2:
        return renderFileUpload();
      case 3:
        return renderCourseNaming();
      case 4:
        return renderProcessing();
      default:
        console.warn('❌ Unknown step requested:', currentStep);
        return <div>Unknown step</div>;
    }
  };

  // Step 1: Analysis Type Selection
  const renderAnalysisTypeSelection = () => (
    <div className="indirect_method_ImportSurveysModal_step_content">
      <h3 className="indirect_method_ImportSurveysModal_step_title">How would you like to organize your survey data?</h3>
      
      <div className="indirect_method_ImportSurveysModal_analysis_options">
        <div 
          className={`indirect_method_ImportSurveysModal_analysis_option ${analysisType === 'by_program' ? 'selected' : ''}`}
          onClick={() => handleAnalysisTypeSelect('by_program')}
        >
          <div className="indirect_method_ImportSurveysModal_option_icon">🎓</div>
          <div className="indirect_method_ImportSurveysModal_option_content">
            <h4>By Program</h4>
            <p>All files will be grouped under <strong>{programName}</strong></p>
            <p className="indirect_method_ImportSurveysModal_option_note">Perfect for program-level analysis</p>
          </div>
        </div>

        <div 
          className={`indirect_method_ImportSurveysModal_analysis_option ${analysisType === 'by_course' ? 'selected' : ''}`}
          onClick={() => handleAnalysisTypeSelect('by_course')}
        >
          <div className="indirect_method_ImportSurveysModal_option_icon">📚</div>
          <div className="indirect_method_ImportSurveysModal_option_content">
            <h4>By Course</h4>
            <p>Each file represents a specific course</p>
            <p className="indirect_method_ImportSurveysModal_option_note">Great for course comparison</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: File Upload
  const renderFileUpload = () => (
    <div className="indirect_method_ImportSurveysModal_step_content">
      <h3 className="indirect_method_ImportSurveysModal_step_title">
        Upload Survey Files {analysisType === 'by_course' ? '(Max 10 files)' : ''}
      </h3>
      
      {selectedFiles.length === 0 ? (
        <div 
          className={`indirect_method_ImportSurveysModal_upload_zone ${isDragActive ? 'drag_active' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="indirect_method_ImportSurveysModal_upload_icon">📁</div>
          <h4 className="indirect_method_ImportSurveysModal_upload_title">
            Drop your Excel files here
          </h4>
          <p className="indirect_method_ImportSurveysModal_upload_description">
            or click to browse files
          </p>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleInitialFileInputChange}
            className="indirect_method_ImportSurveysModal_file_input"
            id="fileInput"
            multiple
          />
          <label 
            htmlFor="fileInput"
            className="indirect_method_ImportSurveysModal_browse_button"
          >
            Browse Files
          </label>
          <p className="indirect_method_ImportSurveysModal_file_info">
            Supported formats: .xlsx, .xls (max 10MB each, 10 files max)
          </p>
        </div>
      ) : (
        <div className="indirect_method_ImportSurveysModal_file_list">
          <div className="indirect_method_ImportSurveysModal_file_list_header">
            <span>{selectedFiles.length} file(s) selected</span>
            <button 
              className="indirect_method_ImportSurveysModal_add_more_button"
              onClick={() => document.getElementById('addMoreFileInput').click()}
            >
              + Add More
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleAddMoreFileInputChange}
              className="indirect_method_ImportSurveysModal_file_input"
              id="addMoreFileInput"
              multiple
              style={{ display: 'none' }}
            />
          </div>
          
          {selectedFiles.map((file, index) => (
            <div key={index} className="indirect_method_ImportSurveysModal_file_item">
              <div className="indirect_method_ImportSurveysModal_file_icon">📊</div>
              <div className="indirect_method_ImportSurveysModal_file_details">
                <span className="indirect_method_ImportSurveysModal_file_name">{file.name}</span>
                <span className="indirect_method_ImportSurveysModal_file_size">{formatFileSize(file.size)}</span>
              </div>
              <button 
                className="indirect_method_ImportSurveysModal_remove_file_button"
                onClick={() => handleRemoveFile(file)}
                title="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3: Course Naming (only for by_course analysis)
  const renderCourseNaming = () => {
    if (analysisType === 'by_program') {
      // Program configuration for by_program analysis
      return (
        <div className="indirect_method_ImportSurveysModal_step_content">
          <h3 className="indirect_method_ImportSurveysModal_step_title">Configure Program Details</h3>
          <p className="indirect_method_ImportSurveysModal_step_description">
            Specify the program name for this batch upload
          </p>
          
          <div className="indirect_method_ImportSurveysModal_program_configuration">
            <div className="indirect_method_ImportSurveysModal_program_input_group">
              <label htmlFor="program_name" className="indirect_method_ImportSurveysModal_program_label">
                Program Name *
              </label>
              <input
                id="program_name"
                type="text"
                value={programNameInput}
                onChange={(e) => setProgramNameInput(e.target.value)}
                placeholder="e.g., BSM, PhD, MBA"
                className="indirect_method_ImportSurveysModal_program_input"
                required
              />
              {programNameInput && programNameInput.trim() !== '' ? 
                <span className="indirect_method_ImportSurveysModal_input_status">✓</span> : 
                <span className="indirect_method_ImportSurveysModal_input_status warning">⚠</span>
              }
              <small className="indirect_method_ImportSurveysModal_program_help">
                Required identifier for the program
              </small>
            </div>
          </div>

          <div className="indirect_method_ImportSurveysModal_program_help_section">
            <div className="indirect_method_ImportSurveysModal_help_item">
              <span className="indirect_method_ImportSurveysModal_help_icon">🎓</span>
              <span><strong>Program Name:</strong> Required identifier (e.g., BSM, PhD, MBA)</span>
            </div>
            <div className="indirect_method_ImportSurveysModal_help_item">
              <span className="indirect_method_ImportSurveysModal_help_icon">📊</span>
              <span><strong>Batch Upload:</strong> All {selectedFiles.length} files will be analyzed as part of this program</span>
            </div>
          </div>
        </div>
      );
    }

    // Course configuration for by_course analysis (existing implementation)
    return (
      <div className="indirect_method_ImportSurveysModal_step_content">
        <h3 className="indirect_method_ImportSurveysModal_step_title">Configure Course Details</h3>
        <p className="indirect_method_ImportSurveysModal_step_description">
          Specify the course name and optional display name for each uploaded file
        </p>
        
        <div className="indirect_method_ImportSurveysModal_course_table">
          <div className="indirect_method_ImportSurveysModal_course_table_header">
            <span>File Name</span>
            <span>Course Name</span>
            <span>Display Name (Optional)</span>
          </div>
          
          {selectedFiles.map((file, index) => (
            <div key={index} className="indirect_method_ImportSurveysModal_course_row">
              <div className="indirect_method_ImportSurveysModal_course_file">
                <span className="indirect_method_ImportSurveysModal_file_icon">📊</span>
                <span className="indirect_method_ImportSurveysModal_file_name">{file.name}</span>
              </div>
              
              {/* Course Name Input */}
              <div className="indirect_method_ImportSurveysModal_course_input_container">
                <input
                  type="text"
                  value={courseMapping[file.name]?.courseName || ''}
                  onChange={(e) => handleCourseNameChange(file.name, e.target.value)}
                  placeholder="e.g., MATH-101"
                  className="indirect_method_ImportSurveysModal_course_input"
                />
                {courseMapping[file.name]?.courseName && courseMapping[file.name].courseName.trim() !== '' ? 
                  <span className="indirect_method_ImportSurveysModal_input_status">✓</span> : 
                  <span className="indirect_method_ImportSurveysModal_input_status warning">⚠</span>
                }
              </div>

              {/* Display Name Input */}
              <div className="indirect_method_ImportSurveysModal_course_input_container">
                <input
                  type="text"
                  value={courseMapping[file.name]?.displayName || ''}
                  onChange={(e) => handleDisplayNameChange(file.name, e.target.value)}
                  placeholder="e.g., Fall 2023 Math Survey"
                  className="indirect_method_ImportSurveysModal_course_input"
                />
                <span className="indirect_method_ImportSurveysModal_input_status optional">📝</span>
              </div>
            </div>
          ))}
        </div>

        <div className="indirect_method_ImportSurveysModal_course_help">
          <div className="indirect_method_ImportSurveysModal_help_item">
            <span className="indirect_method_ImportSurveysModal_help_icon">📚</span>
            <span><strong>Course Name:</strong> Required identifier (e.g., MATH-101, PHYS-201)</span>
          </div>
          <div className="indirect_method_ImportSurveysModal_help_item">
            <span className="indirect_method_ImportSurveysModal_help_icon">🏷️</span>
            <span><strong>Display Name:</strong> Optional friendly name for easier identification</span>
          </div>
        </div>
      </div>
    );
  };

  // Step 4: Processing
  const renderProcessing = () => {
    // Get status icon for each file
    const getStatusIcon = (status) => {
      switch (status) {
        case 'completed': return '✅';
        case 'processing': return '🔄';
        case 'failed': return '❌';
        case 'queued': 
        default: return '⏳';
      }
    };

    // Calculate overall progress
    const totalFiles = selectedFiles.length;
    const completedFiles = Object.values(fileStatuses).filter(s => s.status === 'completed').length;
    const failedFiles = Object.values(fileStatuses).filter(s => s.status === 'failed').length;

    return (
      <div className="indirect_method_ImportSurveysModal_step_content">
        <h3 className="indirect_method_ImportSurveysModal_step_title">Processing Survey Data</h3>
        
        {/* Overall Progress */}
        <div className="indirect_method_ImportSurveysModal_processing_summary">
          <div className="indirect_method_ImportSurveysModal_processing_progress">
            <span className="indirect_method_ImportSurveysModal_progress_text">
              {processingStatus || `Processing ${totalFiles} files...`}
            </span>
            <span className="indirect_method_ImportSurveysModal_progress_count">
              ({completedFiles + failedFiles}/{totalFiles} completed)
            </span>
          </div>
        </div>

        {/* Individual File Progress */}
        <div className="indirect_method_ImportSurveysModal_file_progress_list">
          {selectedFiles.map((file, index) => {
            const fileStatus = fileStatuses[file.name] || { status: 'queued', message: 'Queued for processing' };
            const statusIcon = getStatusIcon(fileStatus.status);
            const retryInfo = retryProgress[file.name];
            
            return (
              <div key={index} className={`indirect_method_ImportSurveysModal_file_progress_item ${fileStatus.status}`}>
                <div className="indirect_method_ImportSurveysModal_file_progress_icon">
                  {statusIcon}
                </div>
                <div className="indirect_method_ImportSurveysModal_file_progress_details">
                  <div className="indirect_method_ImportSurveysModal_file_progress_name">
                    {file.name}
                    {retryInfo && retryInfo.attempt > 0 && (
                      <span className="indirect_method_ImportSurveysModal_retry_indicator">
                        (Attempt {retryInfo.attempt}/{retryInfo.maxAttempts})
                      </span>
                    )}
                  </div>
                  <div className="indirect_method_ImportSurveysModal_file_progress_status">
                    {fileStatus.message}
                  </div>
                  {retryInfo && retryInfo.status === 'waiting' && retryInfo.delay > 0 && (
                    <div className="indirect_method_ImportSurveysModal_retry_countdown">
                      Waiting {Math.ceil(retryInfo.delay / 1000)}s before retry...
                    </div>
                  )}
                  {retryInfo && retryInfo.errorCategory && (
                    <div className="indirect_method_ImportSurveysModal_error_category_indicator">
                      {retryInfo.errorCategory} error
                      {retryInfo.isRetryable === false && ' (not retryable)'}
                    </div>
                  )}
                </div>
                {fileStatus.status === 'processing' && (
                  <div className="indirect_method_ImportSurveysModal_file_progress_spinner">
                    <div className="indirect_method_ImportSurveysModal_spinner"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Processing Details */}
        <div className="indirect_method_ImportSurveysModal_processing_details">
          <div className="indirect_method_ImportSurveysModal_processing_detail">
            <span className="indirect_method_ImportSurveysModal_detail_label">Files:</span>
            <span className="indirect_method_ImportSurveysModal_detail_value">{totalFiles}</span>
          </div>
          <div className="indirect_method_ImportSurveysModal_processing_detail">
            <span className="indirect_method_ImportSurveysModal_detail_label">Analysis Type:</span>
            <span className="indirect_method_ImportSurveysModal_detail_value">
              {analysisType === 'by_program' ? 'By Program' : 'By Course'}
            </span>
          </div>
          {analysisType === 'by_course' && (
            <div className="indirect_method_ImportSurveysModal_processing_detail">
              <span className="indirect_method_ImportSurveysModal_detail_label">Courses:</span>
              <span className="indirect_method_ImportSurveysModal_detail_value">
                {Object.values(courseMapping).filter(c => c.courseName && c.courseName.trim() !== '').length}
              </span>
            </div>
          )}
          {completedFiles > 0 && (
            <div className="indirect_method_ImportSurveysModal_processing_detail">
              <span className="indirect_method_ImportSurveysModal_detail_label">Completed:</span>
              <span className="indirect_method_ImportSurveysModal_detail_value success">{completedFiles}</span>
            </div>
          )}
          {failedFiles > 0 && (
            <div className="indirect_method_ImportSurveysModal_processing_detail">
              <span className="indirect_method_ImportSurveysModal_detail_label">Failed:</span>
              <span className="indirect_method_ImportSurveysModal_detail_value error">{failedFiles}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render results
  const renderResults = () => {
    const { successfulFiles, failedFiles, totalFiles, analysisType } = batchResults;
    const hasSuccesses = successfulFiles.length > 0;
    const hasFailures = failedFiles.length > 0;
    
    return (
      <div className="indirect_method_ImportSurveysModal_step_content">
        <h3 className="indirect_method_ImportSurveysModal_step_title">Processing Complete</h3>
        
        {/* Results Summary */}
        <div className="indirect_method_ImportSurveysModal_results_summary">
          <div className="indirect_method_ImportSurveysModal_results_stats">
            <div className="indirect_method_ImportSurveysModal_stat_item success">
              <span className="indirect_method_ImportSurveysModal_stat_number">{successfulFiles.length}</span>
              <span className="indirect_method_ImportSurveysModal_stat_label">Successful</span>
            </div>
            {hasFailures && (
              <div className="indirect_method_ImportSurveysModal_stat_item error">
                <span className="indirect_method_ImportSurveysModal_stat_number">{failedFiles.length}</span>
                <span className="indirect_method_ImportSurveysModal_stat_label">Failed</span>
              </div>
            )}
            <div className="indirect_method_ImportSurveysModal_stat_item">
              <span className="indirect_method_ImportSurveysModal_stat_number">{totalFiles}</span>
              <span className="indirect_method_ImportSurveysModal_stat_label">Total</span>
            </div>
          </div>
        </div>

        {/* Successful Files */}
        {hasSuccesses && (
          <div className="indirect_method_ImportSurveysModal_results_section">
            <h4 className="indirect_method_ImportSurveysModal_section_title success">
              ✅ Successfully Processed ({successfulFiles.length})
            </h4>
            <div className="indirect_method_ImportSurveysModal_results_list">
              {successfulFiles.map((file, index) => (
                <div key={index} className="indirect_method_ImportSurveysModal_result_item success">
                  <div className="indirect_method_ImportSurveysModal_result_info">
                    <div className="indirect_method_ImportSurveysModal_result_name">
                      📊 {file.filename}
                    </div>
                    <div className="indirect_method_ImportSurveysModal_result_details">
                      {analysisType === 'by_course' && batchResults.courseMapping[file.filename] && (
                        <span className="indirect_method_ImportSurveysModal_course_tag">
                          {batchResults.courseMapping[file.filename].display_name || 
                           batchResults.courseMapping[file.filename].course_name}
                        </span>
                      )}
                      <span className="indirect_method_ImportSurveysModal_status_tag success">
                        Analysis Complete
                      </span>
                      {retryProgress[file.filename] && retryProgress[file.filename].attempt > 0 && (
                        <span className="indirect_method_ImportSurveysModal_retry_tag success">
                          Succeeded after {retryProgress[file.filename].attempt} attempt(s)
                        </span>
                      )}
                    </div>
                  </div>
                  {/* <button 
                    className="indirect_method_ImportSurveysModal_view_results_button"
                    onClick={() => handleViewResults(file.analysis_id, file.filename)}
                  >
                    View Results
                  </button> */}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed Files */}
        {hasFailures && (
          <div className="indirect_method_ImportSurveysModal_results_section">
            <h4 className="indirect_method_ImportSurveysModal_section_title error">
              ❌ Processing Failed ({failedFiles.length})
            </h4>
            <div className="indirect_method_ImportSurveysModal_results_list">
              {failedFiles.map((file, index) => (
                <div key={index} className="indirect_method_ImportSurveysModal_result_item error">
                  <div className="indirect_method_ImportSurveysModal_result_info">
                    <div className="indirect_method_ImportSurveysModal_result_name">
                      📊 {file.filename}
                    </div>
                    <div className="indirect_method_ImportSurveysModal_result_details">
                      <span className="indirect_method_ImportSurveysModal_status_tag error">
                        Failed
                      </span>
                      {file.error && (
                        <span className="indirect_method_ImportSurveysModal_error_message">
                          {file.error}
                        </span>
                      )}
                      {retryProgress[file.filename] && (
                        <>
                          {retryProgress[file.filename].attempt > 0 && (
                            <span className="indirect_method_ImportSurveysModal_retry_tag error">
                              {retryProgress[file.filename].attempt} attempt(s) made
                            </span>
                          )}
                          {retryProgress[file.filename].errorCategory && (
                            <span className="indirect_method_ImportSurveysModal_error_category_tag">
                              {retryProgress[file.filename].errorCategory} error
                            </span>
                          )}
                          {retryProgress[file.filename].isRetryable === false && (
                            <span className="indirect_method_ImportSurveysModal_non_retryable_tag">
                              Not retryable
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <button 
                    className="indirect_method_ImportSurveysModal_retry_button"
                    onClick={() => handleRetryFile(file.filename)}
                  >
                    Retry
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="indirect_method_ImportSurveysModal_results_actions">
          {hasSuccesses && (
            <button 
              className="indirect_method_ImportSurveysModal_view_all_button"
              onClick={handleViewAllResults}
            >
              View All Results
            </button>
          )}
          {hasFailures && (
            <button 
              className="indirect_method_ImportSurveysModal_retry_all_button"
              onClick={handleRetryAllFailed}
            >
              Retry All Failed
            </button>
          )}
          <button 
            className="indirect_method_ImportSurveysModal_upload_more_button"
            onClick={handleUploadMore}
          >
            Upload More Files
          </button>
        </div>
      </div>
    );
  };

  // Cancellation utilities
  const addCancellableOperation = useCallback((operationId) => {
    setCancellableOperations(prev => new Set([...prev, operationId]));
  }, []);

  const removeCancellableOperation = useCallback((operationId) => {
    setCancellableOperations(prev => {
      const newSet = new Set(prev);
      newSet.delete(operationId);
      return newSet;
    });
  }, []);

  const isCancellable = useCallback(() => {
    return cancellableOperations.size > 0 && !isCancelling;
  }, [cancellableOperations.size, isCancelling]);

  // Handle cancellation request
  const handleCancellationRequest = useCallback(() => {
    if (!isCancellable()) return;
    
    console.log('🚫 Cancellation requested by user');
    setShowCancellationModal(true);
    announceToScreenReader('Cancellation confirmation dialog opened', 'assertive');
  }, [isCancellable, announceToScreenReader]);

  // Confirm cancellation
  const confirmCancellation = useCallback(async () => {
    console.log('🚫 Cancellation confirmed by user');
    setShowCancellationModal(false);
    setIsCancelling(true);
    setCancellationRequested(true);
    announceToScreenReader('Cancelling upload process...', 'assertive');

    try {
      // Cancel background processes
      backgroundProcesses.forEach(process => {
        openAIService.stopBackgroundProcessing(process.batchId);
      });

      // Clean up states
      await cleanupCancellation();
      
      announceToScreenReader('Upload cancelled successfully', 'assertive');
    } catch (error) {
      console.error('Error during cancellation:', error);
      announceToScreenReader('Cancellation completed with some errors', 'assertive');
    }
  }, [backgroundProcesses, announceToScreenReader]);

  // Cancel cancellation (user changed mind)
  const cancelCancellation = useCallback(() => {
    console.log('↩️ Cancellation cancelled by user');
    setShowCancellationModal(false);
    announceToScreenReader('Cancellation cancelled', 'polite');
  }, [announceToScreenReader]);

  // Clean up after cancellation
  const cleanupCancellation = useCallback(async () => {
    console.log('🧹 Cleaning up after cancellation...');
    
    // Reset processing states
    setIsProcessing(false);
    setProcessingStatus('');
    setShowResults(false);
    
    // Reset file states
    setFileStatuses({});
    setRetryProgress({});
    setBatchResults(null);
    
    // Reset timeout states
    setTimeoutInfo(null);
    setShowTimeoutModal(false);
    
    // Clear background processes
    setBackgroundProcesses([]);
    
    // Clear cancellable operations
    setCancellableOperations(new Set());
    
    // Reset cancellation states
    setIsCancelling(false);
    setCancellationRequested(false);
    
    // Clear any errors
    setError('');
    
    // Optimize memory after cleanup
    setTimeout(() => {
      optimizePerformance();
    }, 1000);
    
    console.log('✅ Cancellation cleanup completed');
  }, [optimizePerformance]);

  // Simplified cancellation logic - always works predictably
  const handleSimpleCancel = useCallback(() => {
    if (selectedFiles.length > 0 || isProcessing || currentStep > 1) {
      // Show confirmation if user has done any work
      console.log('🚫 Simple cancel requested - showing confirmation');
      setShowCancellationModal(true);
      announceToScreenReader('Cancellation confirmation dialog opened', 'assertive');
    } else {
      // Direct close if nothing selected and on first step
      console.log('🚫 Simple cancel - direct close');
      onClose();
    }
  }, [selectedFiles.length, isProcessing, currentStep, announceToScreenReader, onClose]);

  // Simplified cancel confirmation - always resets everything
  const confirmSimpleCancel = useCallback(() => {
    console.log('🚫 Simple cancellation confirmed - resetting everything');
    setShowCancellationModal(false);
    
    // Stop any ongoing processes first
    if (isProcessing) {
      setIsCancelling(true);
      setCancellationRequested(true);
      
      // Cancel background processes
      backgroundProcesses.forEach(process => {
        openAIService.stopBackgroundProcessing(process.batchId);
      });
    }
    
    // Reset ALL state to initial values
    setCurrentStep(1);
    setAnalysisType(null);
    setSelectedFiles([]);
    setCourseMapping({});
    setFileStatuses({});
    setBatchResults(null);
    setIsDragActive(false);
    setIsProcessing(false);
    setError('');
    setProcessingStatus('');
    setShowResults(false);
    setRetryProgress({});
    setTimeoutInfo(null);
    setShowTimeoutModal(false);
    setBackgroundProcesses([]);
    setIsCancelling(false);
    setCancellationRequested(false);
    setCancellableOperations(new Set());
    
    announceToScreenReader('Upload cancelled and reset successfully', 'assertive');
    
    // Close modal after reset
    onClose();
  }, [isProcessing, backgroundProcesses, announceToScreenReader, onClose]);

  // Simple cancel cancellation (user changed mind)
  const cancelSimpleCancel = useCallback(() => {
    console.log('↩️ Simple cancellation cancelled by user');
    setShowCancellationModal(false);
    announceToScreenReader('Cancellation cancelled', 'polite');
  }, [announceToScreenReader]);

  return (
    <div className="indirect_method_ImportSurveysModal_overlay" onClick={onClose}>
      <div 
        className="indirect_method_ImportSurveysModal_container" 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        aria-modal="true"
      >
        {/* Screen Reader Announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          className="indirect_method_ImportSurveysModal_sr_only"
        >
          {announcements
            .filter(a => a.priority === 'polite')
            .map(a => (
              <div key={a.id}>{a.message}</div>
            ))
          }
        </div>
        <div 
          aria-live="assertive" 
          aria-atomic="true" 
          className="indirect_method_ImportSurveysModal_sr_only"
        >
          {announcements
            .filter(a => a.priority === 'assertive')
            .map(a => (
              <div key={a.id}>{a.message}</div>
            ))
          }
        </div>

        {/* Header */}
        <div className="indirect_method_ImportSurveysModal_header">
          <h2 id="modal-title" className="indirect_method_ImportSurveysModal_title">
            Import Survey Data
          </h2>
          <div className="indirect_method_ImportSurveysModal_header_actions">
            {/* Performance Indicator */}
            {performanceMetrics && (
              <div className="indirect_method_ImportSurveysModal_performance_indicator">
                <button
                  className={`indirect_method_ImportSurveysModal_performance_button ${isOptimizing ? 'optimizing' : ''} ${showPerformanceDashboard ? 'active' : ''}`}
                  onClick={() => {
                    if (isOptimizing) return;
                    if (showPerformanceDashboard) {
                      setShowPerformanceDashboard(false);
                    } else {
                      setShowPerformanceDashboard(true);
                      updatePerformanceMetrics();
                    }
                  }}
                  disabled={isOptimizing}
                  title={`Memory: ${performanceMetrics.memory.current ? 
                    Math.round(performanceMetrics.memory.current.used / 1024 / 1024) : 0}MB - Click for details`}
                  aria-label="Performance metrics and optimization"
                  aria-expanded={showPerformanceDashboard}
                >
                  {isOptimizing ? '🔄' : '⚡'}
                </button>
              </div>
            )}
            <button 
              className="indirect_method_ImportSurveysModal_close_button"
              onClick={onClose}
              disabled={isProcessing}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="indirect_method_ImportSurveysModal_progress">
          <div className="indirect_method_ImportSurveysModal_progress_steps">
            {getStepConfig().map(step => {
              const isActive = step.number <= currentStep;
              const isCurrent = step.number === currentStep;
              
              return (
                <div 
                  key={step.number}
                  className={`indirect_method_ImportSurveysModal_progress_step ${
                    isActive ? 'active' : ''
                  } ${isCurrent ? 'current' : ''}`}
                >
                  <div className="indirect_method_ImportSurveysModal_progress_step_number">
                    {step.number}
                  </div>
                  <div className="indirect_method_ImportSurveysModal_progress_step_label">
                    {step.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="indirect_method_ImportSurveysModal_body">
          {/* Performance Dashboard */}
          {renderPerformanceDashboard()}
          
          {renderStepContent()}
          
          {/* Error message */}
          {error && (
            <div className="indirect_method_ImportSurveysModal_error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="indirect_method_ImportSurveysModal_footer">
          <button 
            className={`indirect_method_ImportSurveysModal_cancel_button ${isCancelling ? 'cancelling' : ''}`}
            onClick={handleSimpleCancel}
            disabled={isCancelling}
            aria-label="Cancel upload and close modal"
          >
            {isCancelling ? 'Cancelling...' : 'Cancel'}
          </button>
          
          {!showResults && currentStep > 1 && currentStep < 4 && (
            <button 
              className="indirect_method_ImportSurveysModal_back_button"
              onClick={() => goToStep(currentStep - 1)}
              disabled={isProcessing || isCancelling}
            >
              Back
            </button>
          )}
          
          {!showResults && currentStep < 4 && (
            <button 
              className="indirect_method_ImportSurveysModal_next_button"
              onClick={handleNextStep}
              disabled={!canProceedToNextStep() || isProcessing || isCancelling}
            >
              {currentStep === 2 && analysisType === 'by_program' ? 'Start Processing' : 
               currentStep === 3 ? 'Start Processing' : 'Next'}
            </button>
          )}
        </div>
      </div>

      {/* Timeout Recovery Modal */}
      {showTimeoutModal && timeoutInfo && (
        <div className="indirect_method_ImportSurveysModal_timeout_overlay">
          <div className="indirect_method_ImportSurveysModal_timeout_modal">
            <div className="indirect_method_ImportSurveysModal_timeout_header">
              <h3>⏰ Processing Taking Longer Than Expected</h3>
            </div>
            
            <div className="indirect_method_ImportSurveysModal_timeout_body">
              <p className="indirect_method_ImportSurveysModal_timeout_message">
                {timeoutInfo.message}
              </p>
              
              <div className="indirect_method_ImportSurveysModal_timeout_stats">
                <div className="indirect_method_ImportSurveysModal_timeout_stat">
                  <span className="indirect_method_ImportSurveysModal_timeout_label">Elapsed:</span>
                  <span className="indirect_method_ImportSurveysModal_timeout_value">
                    {Math.round(timeoutInfo.elapsed / 1000)}s
                  </span>
                </div>
                <div className="indirect_method_ImportSurveysModal_timeout_stat">
                  <span className="indirect_method_ImportSurveysModal_timeout_label">Remaining:</span>
                  <span className="indirect_method_ImportSurveysModal_timeout_value">
                    {Math.round(timeoutInfo.remaining / 1000)}s
                  </span>
                </div>
              </div>

              <div className="indirect_method_ImportSurveysModal_timeout_options">
                <p>What would you like to do?</p>
                
                <button 
                  className="indirect_method_ImportSurveysModal_timeout_option continue"
                  onClick={() => handleTimeoutAction('continue')}
                >
                  <span className="indirect_method_ImportSurveysModal_timeout_icon">⏳</span>
                  <div>
                    <strong>Continue Waiting</strong>
                    <p>Keep processing in the foreground</p>
                  </div>
                </button>

                <button 
                  className="indirect_method_ImportSurveysModal_timeout_option background"
                  onClick={() => handleTimeoutAction('background')}
                >
                  <span className="indirect_method_ImportSurveysModal_timeout_icon">🔄</span>
                  <div>
                    <strong>Move to Background</strong>
                    <p>Continue processing while you work on other things</p>
                  </div>
                </button>

                <button 
                  className="indirect_method_ImportSurveysModal_timeout_option cancel"
                  onClick={() => handleTimeoutAction('cancel')}
                >
                  <span className="indirect_method_ImportSurveysModal_timeout_icon">❌</span>
                  <div>
                    <strong>Cancel Processing</strong>
                    <p>Stop processing and return to upload</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Confirmation Modal */}
      {showCancellationModal && (
        <div className="indirect_method_ImportSurveysModal_cancellation_overlay">
          <div className="indirect_method_ImportSurveysModal_cancellation_modal">
            <div className="indirect_method_ImportSurveysModal_cancellation_header">
              <h3>🚫 Cancel Upload?</h3>
            </div>
            
            <div className="indirect_method_ImportSurveysModal_cancellation_body">
              <p className="indirect_method_ImportSurveysModal_cancellation_message">
                Are you sure you want to cancel and reset the upload process?
              </p>
              
              <div className="indirect_method_ImportSurveysModal_cancellation_details">
                <div className="indirect_method_ImportSurveysModal_cancellation_warning">
                  <span className="indirect_method_ImportSurveysModal_warning_icon">⚠️</span>
                  <div>
                    <strong>This will:</strong>
                    <ul>
                      <li>Stop all file processing</li>
                      <li>Clear all selected files</li>
                      <li>Reset all settings</li>
                      <li>Close the modal</li>
                    </ul>
                  </div>
                </div>
                
                {selectedFiles.length > 0 && (
                  <div className="indirect_method_ImportSurveysModal_cancellation_info">
                    <span className="indirect_method_ImportSurveysModal_info_icon">📊</span>
                    <div>
                      <strong>Current Upload:</strong>
                      <p>{selectedFiles.length} file(s) selected</p>
                      {isProcessing && (
                        <p className="indirect_method_ImportSurveysModal_processing_note">
                          Processing in progress...
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="indirect_method_ImportSurveysModal_cancellation_actions">
                <button 
                  className="indirect_method_ImportSurveysModal_cancellation_keep"
                  onClick={cancelSimpleCancel}
                  autoFocus
                >
                  Keep Working
                </button>
                
                <button 
                  className="indirect_method_ImportSurveysModal_cancellation_confirm"
                  onClick={confirmSimpleCancel}
                >
                  Yes, Cancel & Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndirectMethod_ImportSurveysModal; 